import { apiRequest } from '../../lib/api';
import {
  announcementResultSchema,
  buyerFeedSchema,
  chatResetResultSchema,
  mentionMutationResultSchema,
  messageActionResultSchema,
  moderationMessagesSchema,
  pinAnswerResultSchema,
  pinQueueSchema,
  replyResultSchema,
  runtimeSchema,
  snippetLibrarySchema,
  snippetSaveResultSchema,
  okSchema,
} from '../../lib/schemas';
import type {
  AnnouncementResult,
  BuyerFeed,
  ModerationMessage,
  PinQueueItem,
  SnippetLibrary,
} from '../../types';

export function getModerationMessages(signal?: AbortSignal): Promise<ModerationMessage[]> {
  return apiRequest('/api/messages', moderationMessagesSchema, signal ? { signal } : {});
}

export function getModerationMessagesAfter(afterId: number, signal?: AbortSignal): Promise<ModerationMessage[]> {
  const options = signal ? { signal } : {};
  return apiRequest(`/api/messages?after_id=${encodeURIComponent(afterId)}`, moderationMessagesSchema, options);
}

export function getAnsweredMessages(signal?: AbortSignal): Promise<ModerationMessage[]> {
  return apiRequest('/api/messages/answered', moderationMessagesSchema, signal ? { signal } : {});
}

export function getPinQueue(signal?: AbortSignal): Promise<PinQueueItem[]> {
  return apiRequest('/api/pins', pinQueueSchema, signal ? { signal } : {});
}

export function resetChat() {
  return apiRequest('/api/messages/reset', chatResetResultSchema, { method: 'POST' });
}

export function getModerationRuntime(signal?: AbortSignal): Promise<{ localDemo: boolean }> {
  return apiRequest('/api/runtime', runtimeSchema, signal ? { signal } : {});
}

export function getSnippets(signal?: AbortSignal): Promise<SnippetLibrary> {
  return apiRequest('/api/snippets', snippetLibrarySchema, signal ? { signal } : {});
}

export function getBuyerFeed(limit: number, signal?: AbortSignal): Promise<BuyerFeed> {
  return apiRequest(`/api/sales/buyers?limit=${encodeURIComponent(limit)}`, buyerFeedSchema, signal ? { signal } : {});
}

export function toggleHighlight(messageId: number) {
  return apiRequest(`/api/messages/${messageId}/highlight`, messageActionResultSchema, { method: 'POST' });
}

export function deleteMessage(messageId: number) {
  return apiRequest(`/api/messages/${messageId}/delete`, messageActionResultSchema, { method: 'POST' });
}

export function pinMessage(messageId: number) {
  return apiRequest(`/api/messages/${messageId}/pin`, messageActionResultSchema, { method: 'POST' });
}

export function markMessageAnswered(messageId: number) {
  return apiRequest(`/api/messages/${messageId}/answer`, messageActionResultSchema, { method: 'POST' });
}

export function reopenMessage(messageId: number) {
  return apiRequest(`/api/messages/${messageId}/reopen`, messageActionResultSchema, { method: 'POST' });
}

export function replyToMessage(messageId: number, text: string, privateReply: boolean) {
  return apiRequest(`/api/messages/${messageId}/reply`, replyResultSchema, {
    method: 'POST',
    body: JSON.stringify({ text, private: privateReply }),
  });
}

export function answerPin(pinId: number) {
  return apiRequest(`/api/pins/${pinId}/answer`, pinAnswerResultSchema, { method: 'POST' });
}

export function announce(text: string, eventIds: number[]): Promise<AnnouncementResult> {
  return apiRequest('/api/announce', announcementResultSchema, {
    method: 'POST',
    body: JSON.stringify({ text, eventIds }),
  });
}

export function saveSnippet(body: string, label: string) {
  return apiRequest('/api/snippets', snippetSaveResultSchema, {
    method: 'POST',
    body: JSON.stringify({ body, label }),
  });
}

export function recordSnippetUse(snippetId: number) {
  return apiRequest(`/api/snippets/${snippetId}/used`, okSchema, { method: 'POST' });
}

export function removeSnippet(snippetId: number) {
  return apiRequest(`/api/snippets/${snippetId}`, okSchema, { method: 'DELETE' });
}

export function addBuyerMention(buyer: { id: string; name: string; product: string; at: number | null }) {
  return apiRequest('/api/sales/mentions', mentionMutationResultSchema, {
    method: 'POST',
    body: JSON.stringify(buyer),
  });
}

export function removeBuyerMention(id: string) {
  return apiRequest('/api/sales/mentions/remove', mentionMutationResultSchema, {
    method: 'POST',
    body: JSON.stringify({ id }),
  });
}

export function sendTvMessage(text: string) {
  return apiRequest('/api/sales/mentions/message', okSchema, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

export function clearTvShoutouts() {
  return apiRequest('/api/sales/mentions/clear', mentionMutationResultSchema, { method: 'POST' });
}
